// RoastLog
import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { sivetzSettings } from '$lib/PersistedState.svelte';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { BaseDevice } from '../base';

// Others
import { invoke } from '@tauri-apps/api/core';

class ModbusClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SivetzClientError';
  }
}

class SivetzDevice extends BaseDevice {
  type: DeviceType = DeviceType.SIVETZ;
  private burnerPower: number | null = null;

  protected async onConnect(): Promise<boolean> {
    try {
      await invoke('connect_modbus', {
        host: sivetzSettings.current.host,
        port: this.getModbusPort()
      });
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Sivetz device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_modbus', {
      host: sivetzSettings.current.host,
      port: this.getModbusPort()
    }).catch((error) => logger.warn(`Disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      // The three Sivetz temperatures are in a block of 6 registers, 2 block per temp.
      const modbusTemps = await this.readTemperatureHolding(
        sivetzSettings.current.host,
        parseInt(sivetzSettings.current.register),
        6
      );

      // const burner = await this.readPowerHolding(sivetzSettings.current.host, 28682);
      this.burnerPower = null;

      this.localReadings = [
        modbusTemps.bean,
        this.tcSettings?.[1].active && modbusTemps.inlet ? modbusTemps.inlet : null,
        this.tcSettings?.[2].active && modbusTemps.exhaust ? modbusTemps.exhaust : null,
        null
      ];
    } catch (error) {
      if (error instanceof ModbusClientError) {
        logger.error(`Modbus error: ${error.message}`);
      } else {
        logger.error(`Unknown error: ${error}`);
      }
      throw error;
    }
  }

  protected async getPowerLevel(): Promise<number | null> {
    return this.burnerPower;
  }

  private async readPowerHolding(host: string, address: number): Promise<number | null> {
    try {
      const results: number[] = await invoke('read_modbus', {
        host,
        port: this.getModbusPort(),
        reg: address,
        count: 2
      });
      return parseInt(results[0].toString(16));
    } catch (error: unknown) {
      return null;
    }
  }

  private async readTemperatureHolding(host: string, address: number, count: number) {
    try {
      const results: number[] = await invoke('read_modbus', {
        host,
        port: this.getModbusPort(),
        reg: address,
        count
      });

      const bean = SivetzDevice.convertTemperature(results[2], results[3]);
      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_SIVETZ_READINGS', { results, bean });
      }

      return {
        bean,
        inlet: SivetzDevice.convertTemperature(results[0], results[1]),
        exhaust: SivetzDevice.convertTemperature(results[4], results[5])
      };
    } catch (err: any) {
      const regex = /error creating modbus client/i;
      if (regex.test(err)) {
        throw new ModbusClientError('Could not create Modbus client');
      } else {
        logger.warn(`Modbus reading error: ${err}`);
      }
      return {
        bean: 0,
        inlet: null,
        exhaust: null
      };
    }
  }

  /**
   * Converts two 16-bit Modbus registers to a 32-bit float
   * using Big-Little endianness (for Modicon devices)
   */
  static convertTemperature(r1: number, r2: number): number {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);

    view.setUint16(0, r2, false);
    view.setUint16(2, r1, false);

    return view.getFloat32(0, false);
  }
}

export default SivetzDevice;
